import { useState, useCallback, useEffect } from 'react'
import {
  Table,
  TextInput,
  Select,
  Group,
  Paper,
  Text,
  Badge,
  ActionIcon,
  Pagination,
  Button,
  Affix,
  Transition,
  LoadingOverlay,
  Tooltip,
  Switch,
  SimpleGrid,
  RangeSlider,
  ThemeIcon,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useDisclosure } from '@mantine/hooks'
import {
  IconSearch,
  IconSortAscending,
  IconSortDescending,
  IconFilter,
  IconFilterOff,
  IconDeviceFloppy,
  IconX,
  IconAlertTriangle,
  IconCheck,
  IconDatabase,
  IconClock,
  IconShieldCheck,
} from '@tabler/icons-react'
import { useChargeLabels, useChargeLabelBatchUpdate } from '@/hooks/useLabeling'
import {
  CHARGE_CATEGORIES,
  EXTRA_CHARGE_CATEGORIES,
  type ChargeLabelQuery,
  type ChargeLabelRow,
  type LabelStatus,
} from '@/services/labeling.api'

// Grouped options for the per-row label picker: the 25 the model knows, plus
// reviewer-requested extras kept in their own group (data only, not in-model).
const CHARGE_LABEL_OPTIONS = [
  { group: 'Model categories', items: [...CHARGE_CATEGORIES].sort() as unknown as string[] },
  { group: 'Proposed / new (not in model)', items: [...EXTRA_CHARGE_CATEGORIES].sort() as unknown as string[] },
]

const COHORT_OPTIONS = [{ value: 'eoy_2026_gap', label: 'EOY gap' }]

interface PendingEdit {
  human_label: string | null
  label_status: LabelStatus
}

const STATUS_COLORS: Record<LabelStatus, string> = {
  PENDING: 'gray',
  LABELED: 'blue',
  CONFIRMED: 'green',
  REJECTED: 'red',
  RELABELED: 'violet',
}

export function StatusBadge({ status }: { status: LabelStatus }) {
  return (
    <Badge color={STATUS_COLORS[status]} variant="light" size="sm">
      {status}
    </Badge>
  )
}

function ConfidenceBadge({ value }: { value: number | null }) {
  if (value === null || value === undefined) return <Text size="xs" c="dimmed">N/A</Text>
  const color = value < 0.5 ? 'red' : value < 0.65 ? 'orange' : 'green'
  return (
    <Badge color={color} variant="light" size="sm">
      {(value * 100).toFixed(1)}%
    </Badge>
  )
}

export function ChargeLabelTable() {
  const [query, setQuery] = useState<ChargeLabelQuery>({
    page: 1,
    pageSize: 50,
    sortBy: 'frequency',
    sortOrder: 'desc',
  })

  const [searchInput, setSearchInput] = useState('')
  const [confidenceRange, setConfidenceRange] = useState<[number, number]>([0, 1])
  const [showFilters, { toggle: toggleFilters }] = useDisclosure(true)
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingEdit>>(new Map())

  const { data, isLoading, isFetching } = useChargeLabels(query)
  const batchUpdate = useChargeLabelBatchUpdate()

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const stats = data?.stats
  const totalPages = Math.ceil(total / query.pageSize)
  const hasPendingEdits = pendingEdits.size > 0

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (pendingEdits.size > 0) e.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [pendingEdits.size])

  const confirmDiscard = useCallback(() => {
    if (!hasPendingEdits) return true
    if (window.confirm(`You have ${pendingEdits.size} unsaved changes. Discard them?`)) {
      setPendingEdits(new Map())
      return true
    }
    return false
  }, [hasPendingEdits, pendingEdits.size])

  const handleSearch = useCallback(() => {
    if (!confirmDiscard()) return
    setQuery(q => ({ ...q, page: 1, search: searchInput || undefined }))
  }, [searchInput, confirmDiscard])

  const handleSort = useCallback((column: string) => {
    setQuery(q => ({
      ...q,
      sortBy: column,
      sortOrder: q.sortBy === column && q.sortOrder === 'desc' ? 'asc' : 'desc',
    }))
  }, [])

  const handlePageChange = useCallback((page: number) => {
    if (!confirmDiscard()) return
    setQuery(q => ({ ...q, page }))
  }, [confirmDiscard])

  const handleFilterChange = useCallback((partial: Partial<ChargeLabelQuery>) => {
    if (!confirmDiscard()) return
    setQuery(q => ({ ...q, ...partial, page: 1 }))
  }, [confirmDiscard])

  const handleClearFilters = useCallback(() => {
    setSearchInput('')
    setConfidenceRange([0, 1])
    setQuery({ page: 1, pageSize: query.pageSize, sortBy: 'frequency', sortOrder: 'desc' })
  }, [query.pageSize])

  const getDisplayLabel = useCallback((row: ChargeLabelRow): string | null => {
    const edit = pendingEdits.get(row.charge_hash)
    if (edit) return edit.human_label
    return row.human_label ?? row.predicted_category
  }, [pendingEdits])

  const getDisplayStatus = useCallback((row: ChargeLabelRow): LabelStatus => {
    return pendingEdits.get(row.charge_hash)?.label_status ?? row.label_status
  }, [pendingEdits])

  const setEdit = useCallback((row: ChargeLabelRow, edit: PendingEdit | null) => {
    setPendingEdits(prev => {
      const next = new Map(prev)
      // No-op if the edit matches what's already persisted -> drop it.
      if (
        edit &&
        edit.human_label === row.human_label &&
        edit.label_status === row.label_status
      ) {
        next.delete(row.charge_hash)
      } else if (edit) {
        next.set(row.charge_hash, edit)
      } else {
        next.delete(row.charge_hash)
      }
      return next
    })
  }, [])

  const handleSelectChange = useCallback((row: ChargeLabelRow, value: string | null) => {
    if (!value) return setEdit(row, null)
    const status: LabelStatus =
      value === row.predicted_category
        ? 'CONFIRMED'
        : row.human_label
          ? 'RELABELED'
          : 'LABELED'
    setEdit(row, { human_label: value, label_status: status })
  }, [setEdit])

  const handleConfirm = useCallback((row: ChargeLabelRow) => {
    const current = getDisplayStatus(row)
    if (current === 'CONFIRMED') {
      setEdit(row, null)
    } else {
      setEdit(row, {
        human_label: getDisplayLabel(row),
        label_status: 'CONFIRMED',
      })
    }
  }, [getDisplayStatus, getDisplayLabel, setEdit])

  const handleReject = useCallback((row: ChargeLabelRow) => {
    const current = getDisplayStatus(row)
    if (current === 'REJECTED') {
      setEdit(row, null)
    } else {
      setEdit(row, { human_label: null, label_status: 'REJECTED' })
    }
  }, [getDisplayStatus, setEdit])

  const handleSaveAll = useCallback(async () => {
    const updates = Array.from(pendingEdits.entries()).map(([charge_hash, edit]) => ({
      charge_hash,
      human_label: edit.human_label,
      label_status: edit.label_status,
    }))
    try {
      const result = await batchUpdate.mutateAsync(updates)
      if (result.totalErrors > 0) {
        const remaining = new Map<string, PendingEdit>()
        for (const id of result.errorIds) {
          const edit = pendingEdits.get(id)
          if (edit) remaining.set(id, edit)
        }
        setPendingEdits(remaining)
        notifications.show({
          title: 'Partial save',
          message: `${result.totalUpdated} saved, ${result.totalErrors} errors`,
          color: 'yellow',
          icon: <IconAlertTriangle size={16} />,
        })
      } else {
        setPendingEdits(new Map())
        notifications.show({
          title: 'Labels saved',
          message: `${result.totalUpdated} charge${result.totalUpdated !== 1 ? 's' : ''} updated`,
          color: 'green',
          icon: <IconCheck size={16} />,
        })
      }
    } catch {
      notifications.show({
        title: 'Save failed',
        message: 'Could not save changes. Please try again.',
        color: 'red',
        icon: <IconX size={16} />,
      })
    }
  }, [pendingEdits, batchUpdate])

  const SortIcon = ({ column }: { column: string }) => {
    if (query.sortBy !== column) return null
    return query.sortOrder === 'asc'
      ? <IconSortAscending size={14} />
      : <IconSortDescending size={14} />
  }

  const sortableHeader = (label: string, column: string) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(column)}>
      <Group gap={4} wrap="nowrap">
        <Text size="xs" fw={600}>{label}</Text>
        <SortIcon column={column} />
      </Group>
    </Table.Th>
  )

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading && !data} />

      {stats && (
        <SimpleGrid cols={{ base: 2, sm: query.cohort ? 7 : 5 }} mb="md">
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="blue"><IconDatabase size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Total Charges</Text>
            </Group>
            <Text size="xl" fw={700}>{stats.totalRows.toLocaleString()}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="gray"><IconClock size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Pending</Text>
            </Group>
            <Text size="xl" fw={700}>{stats.pendingCount.toLocaleString()}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Confirmed</Text>
            </Group>
            <Text size="xl" fw={700} c="green">{stats.confirmedCount.toLocaleString()}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="orange"><IconAlertTriangle size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Low Conf (&lt;0.65)</Text>
            </Group>
            <Text size="xl" fw={700} c="orange">{(stats.lowConfidenceCount ?? 0).toLocaleString()}</Text>
          </Paper>
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="teal"><IconShieldCheck size={14} /></ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Ground Truth</Text>
            </Group>
            <Text size="xl" fw={700} c="teal">{stats.groundTruthCount.toLocaleString()}</Text>
          </Paper>
          {query.cohort && (
            <>
              <Paper p="md" radius="md" withBorder>
                <Group gap="xs" mb={4}>
                  <ThemeIcon size="sm" variant="light" color="gray"><IconClock size={14} /></ThemeIcon>
                  <Text size="xs" c="dimmed" fw={500}>EOY Remaining</Text>
                </Group>
                <Text size="xl" fw={700}>{(stats.cohortRemainingCount ?? 0).toLocaleString()}</Text>
              </Paper>
              <Paper p="md" radius="md" withBorder>
                <Group gap="xs" mb={4}>
                  <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={14} /></ThemeIcon>
                  <Text size="xs" c="dimmed" fw={500}>EOY Labeled</Text>
                </Group>
                <Text size="xl" fw={700} c="green">{(stats.cohortLabeledCount ?? 0).toLocaleString()}</Text>
              </Paper>
            </>
          )}
        </SimpleGrid>
      )}

      <Paper p="sm" radius="md" withBorder mb="md">
        <Group justify="space-between" mb={showFilters ? 'sm' : 0}>
          <Group gap="sm">
            <TextInput
              placeholder="Search charges..."
              leftSection={<IconSearch size={16} />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              size="sm"
              style={{ minWidth: 280 }}
            />
            <Button size="sm" variant="light" onClick={handleSearch}>Search</Button>
          </Group>
          <Group gap="sm">
            <ActionIcon variant="subtle" onClick={toggleFilters} title="Toggle filters">
              {showFilters ? <IconFilterOff size={18} /> : <IconFilter size={18} />}
            </ActionIcon>
            <Button size="xs" variant="subtle" color="gray" onClick={handleClearFilters}>Clear All</Button>
          </Group>
        </Group>

        {showFilters && (
          <Group gap="md" align="flex-end">
            <Select
              label="Predicted Category"
              placeholder="All"
              size="xs"
              clearable
              searchable
              data={[...CHARGE_CATEGORIES].sort() as unknown as string[]}
              value={query.predictedCategory ?? null}
              onChange={(val) => handleFilterChange({ predictedCategory: val || undefined })}
              style={{ minWidth: 170 }}
            />
            <Select
              label="Status"
              placeholder="All"
              size="xs"
              clearable
              data={['PENDING', 'LABELED', 'CONFIRMED', 'REJECTED', 'RELABELED']}
              value={query.labelStatus ?? null}
              onChange={(val) => handleFilterChange({ labelStatus: val || undefined })}
              style={{ minWidth: 140 }}
            />
            <Select
              label="Queue"
              placeholder="All"
              size="xs"
              clearable
              data={COHORT_OPTIONS}
              value={query.cohort ?? null}
              onChange={(val) => handleFilterChange({ cohort: val || undefined })}
              style={{ minWidth: 140 }}
            />
            <div style={{ minWidth: 200 }}>
              <Text size="xs" fw={500} mb={4}>Confidence Range</Text>
              <RangeSlider
                min={0}
                max={1}
                step={0.05}
                minRange={0.05}
                value={confidenceRange}
                onChange={setConfidenceRange}
                onChangeEnd={(val) => handleFilterChange({
                  confidenceMin: val[0] > 0 ? val[0] : undefined,
                  confidenceMax: val[1] < 1 ? val[1] : undefined,
                })}
                marks={[{ value: 0, label: '0' }, { value: 0.65, label: '0.65' }, { value: 1, label: '1' }]}
                size="sm"
                label={(v) => `${(v * 100).toFixed(0)}%`}
              />
            </div>
            <Switch
              label="Ground Truth only"
              size="xs"
              checked={query.isGroundTruth === true}
              onChange={(e) => handleFilterChange({ isGroundTruth: e.currentTarget.checked ? true : undefined })}
            />
          </Group>
        )}
      </Paper>

      <Group justify="space-between" mb="xs">
        <Text size="sm" c="dimmed">
          {isFetching ? 'Loading...' : `${total.toLocaleString()} results`}
          {query.page > 1 && ` (page ${query.page})`}
        </Text>
        <Select
          size="xs"
          value={String(query.pageSize)}
          onChange={(val) => handleFilterChange({ pageSize: parseInt(val!) })}
          data={['25', '50', '100']}
          style={{ width: 80 }}
        />
      </Group>

      <Paper withBorder radius="md" style={{ overflow: 'auto' }}>
        <Table striped highlightOnHover withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              {sortableHeader('Charge', 'charge_clean')}
              {sortableHeader('Predicted', 'predicted_category')}
              {sortableHeader('Confidence', 'predicted_confidence')}
              <Table.Th style={{ minWidth: 220 }}><Text size="xs" fw={600}>Human Label</Text></Table.Th>
              <Table.Th><Text size="xs" fw={600}>Status</Text></Table.Th>
              <Table.Th style={{ width: 90 }}><Text size="xs" fw={600}>Actions</Text></Table.Th>
              {sortableHeader('Freq', 'frequency')}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && !isLoading && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text ta="center" c="dimmed" py="xl">No records found</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((row) => {
              const edit = pendingEdits.get(row.charge_hash)
              const isEdited = !!edit
              const displayStatus = getDisplayStatus(row)
              const isLowConfidence = row.predicted_confidence !== null && row.predicted_confidence < 0.65
              const isRejected = displayStatus === 'REJECTED'

              return (
                <Table.Tr
                  key={row.charge_hash}
                  style={{
                    borderLeft: isEdited ? '3px solid var(--mantine-color-blue-5)' : undefined,
                    backgroundColor: isEdited
                      ? 'var(--mantine-color-blue-0)'
                      : isLowConfidence
                        ? 'var(--mantine-color-orange-0)'
                        : undefined,
                  }}
                >
                  <Table.Td style={{ maxWidth: 320 }}>
                    <Tooltip
                      label={`${row.charge_clean}${row.statute_clean ? ' · ' + row.statute_clean : ''}${row.state_clean ? ' · ' + row.state_clean : ''}`}
                      multiline
                      maw={400}
                    >
                      <div>
                        <Text size="sm" lineClamp={2}>{row.charge_clean}</Text>
                        {(row.statute_clean || row.state_clean) && (
                          <Text size="xs" c="dimmed">
                            {[row.statute_clean, row.state_clean].filter(Boolean).join(' · ')}
                          </Text>
                        )}
                      </div>
                    </Tooltip>
                  </Table.Td>

                  <Table.Td>
                    {row.predicted_category
                      ? <Badge variant="light" color="gray" size="sm">{row.predicted_category}</Badge>
                      : <Text size="xs" c="dimmed">-</Text>}
                  </Table.Td>

                  <Table.Td><ConfidenceBadge value={row.predicted_confidence} /></Table.Td>

                  <Table.Td>
                    <Select
                      size="xs"
                      searchable
                      placeholder="Pick label"
                      data={CHARGE_LABEL_OPTIONS}
                      value={isRejected ? null : getDisplayLabel(row)}
                      disabled={isRejected}
                      onChange={(val) => handleSelectChange(row, val)}
                      comboboxProps={{ withinPortal: true }}
                    />
                  </Table.Td>

                  <Table.Td><StatusBadge status={displayStatus} /></Table.Td>

                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label={displayStatus === 'CONFIRMED' ? 'Undo confirm' : 'Confirm label'}>
                        <ActionIcon
                          size="sm"
                          variant={displayStatus === 'CONFIRMED' ? 'filled' : 'subtle'}
                          color="green"
                          onClick={() => handleConfirm(row)}
                        >
                          <IconCheck size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={isRejected ? 'Undo reject' : 'Reject (unusable)'}>
                        <ActionIcon
                          size="sm"
                          variant={isRejected ? 'filled' : 'subtle'}
                          color="red"
                          onClick={() => handleReject(row)}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>

                  <Table.Td><Text size="xs">{row.frequency ?? '-'}</Text></Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={query.page} onChange={handlePageChange} size="sm" />
        </Group>
      )}

      <Affix position={{ bottom: 20, left: '50%' }} style={{ transform: 'translateX(-50%)' }}>
        <Transition mounted={hasPendingEdits} transition="slide-up">
          {(styles) => (
            <Paper style={styles} shadow="xl" radius="lg" p="sm" px="lg" withBorder>
              <Group gap="md">
                <Badge size="lg" variant="filled" color="blue" circle>{pendingEdits.size}</Badge>
                <Text size="sm" fw={500}>unsaved change{pendingEdits.size !== 1 ? 's' : ''}</Text>
                <Button
                  size="sm"
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={handleSaveAll}
                  loading={batchUpdate.isPending}
                >
                  Save All
                </Button>
                <Button
                  size="sm"
                  variant="subtle"
                  color="gray"
                  leftSection={<IconX size={16} />}
                  onClick={() => setPendingEdits(new Map())}
                  disabled={batchUpdate.isPending}
                >
                  Discard
                </Button>
              </Group>
            </Paper>
          )}
        </Transition>
      </Affix>
    </div>
  )
}
